// App shell: providers, hash routes (deep links like /#/stage/ch08?tier=rationed), error boundary,
// storage banner and the "Update ready" toast (§9.13).
import { Component, type ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { Route, Router, Switch, useLocation } from 'wouter-preact';
import { useHashLocation } from 'wouter-preact/use-hash-location';
import { audio } from '@audio/engine.ts';
import { setVoicesEnabled } from '@audio/barks.ts';
import { loadStage } from '@content/stages/index.ts';
import type { StageDef } from '@content/types.ts';
import { TIERS, type Tier } from '@prompts/types.ts';
import { storageUnavailable } from '@save/store.ts';
import { Button } from '@ui/core.tsx';
import { ToastHost } from '@ui/toast.tsx';
import { acceptUpdate, updateReady } from './sw-register.ts';
import { applySettingsToDocument, boot, flushSave, ready, save, settings, tier as savedTier } from './state.ts';
import { ROUTES } from './routes.tsx';
import type StageWrapperDefault from './StageWrapper.tsx';

type StageWrapperType = typeof StageWrapperDefault;

class ErrorBoundary extends Component<{ children: ComponentChildren }, { error: Error | null }> {
  override state = { error: null as Error | null };
  static override getDerivedStateFromError(error: Error) {
    return { error };
  }
  override render() {
    if (this.state.error) {
      return (
        <main id="main" class="fatal">
          <h1 class="font-title">Something broke in the lab</h1>
          <p class="dim">{this.state.error.message}</p>
          <Button tone="primary" onClick={() => location.reload()}>
            Reload
          </Button>
        </main>
      );
    }
    return this.props.children;
  }
}

/** Hash routing with a query inside the hash: `#/stage/ch08?tier=rationed` (§4.9 teacher deep links). */
function useHashPath(): [string, (to: string) => void] {
  const [loc, navigate] = useHashLocation();
  return [loc.split('?')[0] ?? '/', navigate];
}

export function hashQuery(): URLSearchParams {
  return new URLSearchParams(location.hash.split('?')[1] ?? '');
}

function StageRoute({ id }: { id: string }) {
  const [, navigate] = useLocation();
  const q = hashQuery().get('tier') as Tier | null;
  const tier: Tier = q && TIERS.includes(q) ? q : savedTier.value;
  const [loaded, setLoaded] = useState<{ def: StageDef; Wrapper: StageWrapperType } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setLoaded(null);
    // The stage content and the stage screen (runner, prompts, instruments, kernel) are their own chunks.
    Promise.all([loadStage(id), import('./StageWrapper.tsx')]).then(
      ([def, m]) => setLoaded({ def, Wrapper: m.default }),
      (e: Error) => setError(e.message),
    );
  }, [id]);
  if (error) {
    return (
      <main id="main" class="fatal">
        <p>{error}</p>
        <Button onClick={() => navigate('/chapters')}>Chapters</Button>
      </main>
    );
  }
  if (!loaded) return <main id="main" class="loading" aria-busy="true">Loading the lab…</main>;
  return <loaded.Wrapper def={loaded.def} tier={tier} navigate={navigate} />;
}

export function App() {
  useEffect(() => {
    void boot();
    const off = applySettingsToDocument();
    const unlock = () => audio.unlock();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    const hide = () => document.hidden && void flushSave();
    document.addEventListener('visibilitychange', hide);
    return () => {
      off();
      document.removeEventListener('visibilitychange', hide);
    };
  }, []);
  useEffect(() => {
    const st = settings.value;
    audio.setVolume('voice', st.voiceVolume);
    audio.setVolume('music', st.musicVolume);
    audio.setVolume('ambience', st.ambienceVolume);
    audio.setVolume('sfx', st.sfxVolume);
    setVoicesEnabled(st.voices);
  }, [settings.value]);

  if (!ready.value) return <main id="main" class="loading" aria-busy="true">Opening the basement…</main>;
  void save.value;
  return (
    <ErrorBoundary>
      <a class="skip-link" href="#main">
        Skip to content
      </a>
      {storageUnavailable.value && <div class="banner">Progress won’t be saved on this device.</div>}
      <Router hook={useHashPath}>
        <Switch>
          <Route path="/stage/:id">{(params: { id: string }) => <StageRoute id={params.id} />}</Route>
          {ROUTES.routes.map((r) => (
            <Route key={r.path} path={r.path} component={r.component} />
          ))}
          <Route>{() => <ROUTES.Home />}</Route>
        </Switch>
      </Router>
      <ToastHost />
      {updateReady.value && <UpdateToast />}
    </ErrorBoundary>
  );
}

function UpdateToast() {
  const [location] = useHashLocation();
  if (location.startsWith('/stage/')) return null; // never interrupt a running stage
  return (
    <div class="update-toast" role="status">
      <span>Update ready. Restart?</span>
      <Button tone="primary" onClick={() => void acceptUpdate()}>
        Restart
      </Button>
      <Button tone="ghost" onClick={() => void (updateReady.value = false)}>
        Later
      </Button>
    </div>
  );
}
